<template>
  <div>
    <a-page-header title="诊断日志项管理">
      <template #extra>
        <a-button type="primary" @click="handleCreate">
          <template #icon><PlusOutlined /></template>
          新建日志项
        </a-button>
      </template>
    </a-page-header>

    <a-card style="margin-top: 16px">
      <a-form layout="inline" style="margin-bottom: 16px">
        <a-form-item label="展示阶段">
          <a-select
            v-model:value="filters.stage_id"
            placeholder="请选择展示阶段"
            style="width: 200px"
            allow-clear
            @change="handleFilterChange"
          >
            <a-select-option v-for="stage in stageList" :key="stage.id" :value="stage.id">
              {{ stage.stage_name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item>
          <a-button type="primary" @click="fetchData">查询</a-button>
          <a-button style="margin-left: 8px" @click="handleReset">重置</a-button>
        </a-form-item>
      </a-form>

      <a-table
        :columns="columns"
        :data-source="dataSource"
        :loading="loading"
        :pagination="pagination"
        row-key="id"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'log_text'">
            <div>
              <span>{{ record.log_text }}</span>
              <div v-if="hasStatusText(record)" style="margin-top: 4px; font-size: 12px">
                <span v-if="record.status_success_text" style="color: #52c41a; margin-right: 12px">
                  ✅ {{ record.status_success_text }}
                </span>
                <span v-if="record.status_failed_text" style="color: #ff4d4f; margin-right: 12px">
                  ❌ {{ record.status_failed_text }}
                </span>
                <span v-if="record.status_info_text" style="color: #1890ff; margin-right: 12px">
                  ℹ️ {{ record.status_info_text }}
                </span>
                <span v-if="record.status_warning_text" style="color: #faad14; margin-right: 12px">
                  ⚠️ {{ record.status_warning_text }}
                </span>
              </div>
            </div>
          </template>
          <template v-else-if="column.key === 'log_level'">
            <a-tag :color="getLogLevelColor(record.log_level)">
              {{ record.log_level }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'is_demo'">
            <a-tag v-if="record.is_demo === 1" color="blue">演示</a-tag>
            <span v-else>-</span>
          </template>
          <template v-else-if="column.key === 'action'">
            <a-button type="link" @click="handleEdit(record)">编辑</a-button>
            <a-popconfirm title="确定删除吗？" @confirm="handleDelete(record.id)">
              <a-button type="link" danger>删除</a-button>
            </a-popconfirm>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- 编辑弹窗 -->
    <a-modal
      v-model:open="modalVisible"
      :title="isEdit ? '编辑日志项' : '新建日志项'"
      width="800px"
      @ok="handleSubmit"
      @cancel="handleCancel"
    >
      <a-form :model="formData" :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-form-item label="展示阶段" required>
          <a-select v-model:value="formData.stage_id" placeholder="请选择展示阶段">
            <a-select-option v-for="stage in stageList" :key="stage.id" :value="stage.id">
              {{ stage.stage_name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="日志文本" required>
          <a-textarea
            ref="logTextTextarea"
            v-model:value="formData.log_text"
            :rows="4"
            placeholder="请输入日志文本（支持变量占位符，如 {random_number}、{timestamp}、{fault_phenomenon} 等）"
          />
          <div style="color: #999; font-size: 12px; margin-top: 4px; margin-bottom: 8px">
            支持变量：{random_number}、{random_float}、{timestamp}、{task_id}、{user_id}、{step_id}、{worker_id}、{fault_phenomenon}
          </div>
          <div style="margin-top: 8px">
            <span style="color: #999; font-size: 12px; margin-right: 8px">快速插入组件：</span>
            <a-select
              v-model:value="selectedComponent"
              placeholder="选择组件插入占位符"
              style="width: 300px"
              show-search
              :filter-option="filterComponentOption"
              @change="handleInsertComponent"
            >
              <a-select-option
                v-for="component in componentList"
                :key="component.component_code"
                :value="component.component_code"
              >
                [{{ component.component_series }}] {{ component.component_name }} {{ component.version || '' }}
              </a-select-option>
            </a-select>
            <span style="color: #999; font-size: 12px; margin-left: 8px">
              格式：{component:component_code}
            </span>
          </div>
        </a-form-item>
        <a-form-item label="成功反馈">
          <a-textarea
            v-model:value="formData.status_success_text"
            :rows="2"
            placeholder="请输入成功时的反馈文本（如：初始化完成，支持变量占位符）"
          />
          <div style="color: #999; font-size: 12px; margin-top: 4px">
            成功时会自动添加 ✅ 图标，格式为：日志文本 ✅ 反馈文本
          </div>
        </a-form-item>
        <a-form-item label="失败反馈">
          <a-textarea
            v-model:value="formData.status_failed_text"
            :rows="2"
            placeholder="请输入失败时的反馈文本（如：初始化失败，支持变量占位符）"
          />
          <div style="color: #999; font-size: 12px; margin-top: 4px">
            失败时会自动添加 ❌ 图标，格式为：日志文本 ❌ 反馈文本
          </div>
        </a-form-item>
        <a-form-item label="信息反馈">
          <a-textarea
            v-model:value="formData.status_info_text"
            :rows="2"
            placeholder="请输入信息/提醒时的反馈文本（如：初始化完成，但连接出现断帧，可能不稳定，支持变量占位符）"
          />
          <div style="color: #999; font-size: 12px; margin-top: 4px">
            信息时会自动添加 ℹ️ 图标，格式为：日志文本 ℹ️ 反馈文本
          </div>
        </a-form-item>
        <a-form-item label="告警反馈">
          <a-textarea
            v-model:value="formData.status_warning_text"
            :rows="2"
            placeholder="请输入告警时的反馈文本（支持变量占位符）"
          />
          <div style="color: #999; font-size: 12px; margin-top: 4px">
            告警时会自动添加 ⚠️ 图标，格式为：日志文本 ⚠️ 反馈文本
          </div>
        </a-form-item>
        <a-form-item label="日志级别">
          <a-select v-model:value="formData.log_level">
            <a-select-option value="info">info</a-select-option>
            <a-select-option value="warning">warning</a-select-option>
            <a-select-option value="error">error</a-select-option>
            <a-select-option value="success">success</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="显示顺序">
          <a-input-number
            v-model:value="formData.display_order"
            :min="0"
            placeholder="请输入显示顺序"
            style="width: 100%"
          />
        </a-form-item>
        <a-form-item label="延迟毫秒数">
          <a-input-number
            v-model:value="formData.delay_ms"
            :min="0"
            placeholder="相对于批次开始时间的延迟"
            style="width: 100%"
          />
        </a-form-item>
        <a-form-item label="等待秒数">
          <a-input-number
            v-model:value="formData.wait_seconds"
            :min="0"
            :precision="2"
            placeholder="显示此日志后等待的秒数"
            style="width: 100%"
          />
        </a-form-item>
        <a-form-item label="演示模式">
          <a-switch v-model:checked="formData.is_demo" :checked-value="1" :unchecked-value="0" />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { PlusOutlined } from '@ant-design/icons-vue';
import request from '@/api/request';

export default {
  name: 'DiagnosisLogItem',
  components: {
    PlusOutlined
  },
  data() {
    return {
      loading: false,
      dataSource: [],
      stageList: [],
      componentList: [],
      selectedComponent: undefined,
      filters: {
        stage_id: undefined
      },
      pagination: {
        current: 1,
        pageSize: 20,
        total: 0,
        showTotal: (total) => `共 ${total} 条`
      },
      columns: [
        { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
        { title: '阶段名称', dataIndex: 'stage_name', key: 'stage_name' },
        { title: '日志文本', key: 'log_text', ellipsis: true },
        { title: '日志级别', key: 'log_level', width: 100 },
        { title: '显示顺序', dataIndex: 'display_order', key: 'display_order', width: 100 },
        { title: '延迟(ms)', dataIndex: 'delay_ms', key: 'delay_ms', width: 100 },
        { title: '等待(s)', dataIndex: 'wait_seconds', key: 'wait_seconds', width: 100 },
        { title: '演示', key: 'is_demo', width: 80 },
        { title: '操作', key: 'action', width: 150 }
      ],
      modalVisible: false,
      isEdit: false,
      formData: {
        stage_id: undefined,
        log_text: '',
        status_success_text: '',
        status_failed_text: '',
        status_info_text: '',
        status_warning_text: '',
        log_level: 'info',
        display_order: 0,
        delay_ms: 0,
        wait_seconds: null,
        is_demo: 0
      }
    };
  },
  mounted() {
    // 从 query 参数中读取 stage_id
    const stageId = this.$route.query.stage_id;
    if (stageId) {
      this.filters.stage_id = parseInt(stageId);
    }
    this.fetchStages();
    this.fetchComponents();
    this.fetchData();
  },
  methods: {
    async fetchStages() {
      try {
        const response = await request.get('/diagnosis-display/stages', {
          params: { pageSize: 1000 }
        });
        if (response.code === 200) {
          this.stageList = response.data.items || [];
        }
      } catch (error) {
        console.error('获取阶段列表失败:', error);
      }
    },
    
    async fetchComponents() {
      try {
        const response = await request.get('/diagnosis-display/components', {
          params: { status: 1 }
        });
        if (response.code === 200) {
          this.componentList = response.data || [];
        }
      } catch (error) {
        console.error('获取组件列表失败:', error);
      }
    },
    
    filterComponentOption(input, option) {
      const component = this.componentList.find(c => c.component_code === option.value);
      if (!component) return false;
      const searchText = input.toLowerCase();
      return (
        component.component_code.toLowerCase().includes(searchText) ||
        component.component_series.toLowerCase().includes(searchText) ||
        component.component_name.toLowerCase().includes(searchText) ||
        (component.version && component.version.toLowerCase().includes(searchText))
      );
    },
    
    handleInsertComponent(componentCode) {
      if (!componentCode) return;
      
      const placeholder = `{component:${componentCode}}`;
      const currentText = this.formData.log_text || '';
      const cursorPos = this.$refs.logTextTextarea?.$el?.querySelector('textarea')?.selectionStart || currentText.length;
      
      // 在光标位置插入占位符
      const newText = currentText.slice(0, cursorPos) + placeholder + currentText.slice(cursorPos);
      this.formData.log_text = newText;
      
      // 重置选择器
      this.selectedComponent = undefined;
      
      // 聚焦到文本域并设置光标位置
      this.$nextTick(() => {
        const textarea = this.$refs.logTextTextarea?.$el?.querySelector('textarea');
        if (textarea) {
          textarea.focus();
          const newCursorPos = cursorPos + placeholder.length;
          textarea.setSelectionRange(newCursorPos, newCursorPos);
        }
      });
    },
    
    async fetchData() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.current,
          pageSize: this.pagination.pageSize
        };
        if (this.filters.stage_id) {
          params.stage_id = this.filters.stage_id;
        }
        
        const response = await request.get('/diagnosis-display/log-items', { params });
        
        if (response.code === 200) {
          // 合并阶段名称
          const items = (response.data.items || []).map(item => {
            const stage = this.stageList.find(s => s.id === item.stage_id);
            return {
              ...item,
              stage_name: stage ? stage.stage_name : '-'
            };
          });
          this.dataSource = items;
          this.pagination.total = response.data.total || 0;
        }
      } catch (error) {
        this.$message.error('获取数据失败: ' + error.message);
      } finally {
        this.loading = false;
      }
    },
    
    handleTableChange(pagination) {
      this.pagination.current = pagination.current;
      this.pagination.pageSize = pagination.pageSize;
      this.fetchData();
    },
    
    handleFilterChange() {
      this.pagination.current = 1;
      this.fetchData();
    },
    
    handleReset() {
      this.filters = { stage_id: undefined };
      this.pagination.current = 1;
      this.fetchData();
    },
    
    getLogLevelColor(level) {
      const colorMap = {
        'info': 'blue',
        'warning': 'orange',
        'error': 'red',
        'success': 'green'
      };
      return colorMap[level] || 'default';
    },
    
    // 检查是否有状态反馈文本
    hasStatusText(record) {
      return record.status_success_text || record.status_failed_text || record.status_info_text || record.status_warning_text;
    },
    
    handleCreate() {
      this.isEdit = false;
      this.formData = {
        stage_id: undefined,
        log_text: '',
        status_success_text: '',
        status_failed_text: '',
        status_info_text: '',
        status_warning_text: '',
        log_level: 'info',
        display_order: 0,
        delay_ms: 0,
        wait_seconds: null,
        is_demo: 0
      };
      this.modalVisible = true;
    },
    
    handleEdit(record) {
      this.isEdit = true;
      this.formData = { ...record };
      this.modalVisible = true;
    },
    
    async handleSubmit() {
      try {
        if (this.isEdit) {
          const response = await request.put(`/diagnosis-display/log-items/${this.formData.id}`, this.formData);
          if (response.code === 200) {
            this.$message.success('更新成功');
            this.modalVisible = false;
            this.fetchData();
          }
        } else {
          const response = await request.post('/diagnosis-display/log-items', this.formData);
          if (response.code === 200) {
            this.$message.success('创建成功');
            this.modalVisible = false;
            this.fetchData();
          }
        }
      } catch (error) {
        this.$message.error('操作失败: ' + error.message);
      }
    },
    
    handleCancel() {
      this.modalVisible = false;
      this.formData = {
        stage_id: undefined,
        log_text: '',
        status_success_text: '',
        status_failed_text: '',
        status_info_text: '',
        status_warning_text: '',
        log_level: 'info',
        display_order: 0,
        delay_ms: 0,
        wait_seconds: null,
        is_demo: 0
      };
    },
    
    async handleDelete(id) {
      try {
        const response = await request.delete(`/diagnosis-display/log-items/${id}`);
        if (response.code === 200) {
          this.$message.success('删除成功');
          this.fetchData();
        }
      } catch (error) {
        this.$message.error('删除失败: ' + error.message);
      }
    }
  }
};
</script>

<style scoped>
</style>
